import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BellIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@mui/material';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { financeApi } from '../../api/financeApi';
import { FINANCE_EVENTS } from '../../constants/socketEvents';
import AlertPanel from './AlertPanel';

const AlertBell: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { isConnected, subscribeToVendor, unsubscribeFromVendor, on, off } = useSocket();
  const [unreadCount, setUnreadCount] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await financeApi.getUnreadAlertCount();
      const count = typeof res.data === 'number' ? res.data : res.data?.count ?? 0;
      setUnreadCount(count);
    } catch {
      // Silently fail — alerts are non-critical
    }
  }, []);

  // Initial fetch once per mount (page load / login).
  useEffect(() => {
    if (user?.id) {
      fetchUnreadCount();
    }
  }, [user?.id, fetchUnreadCount]);

  // Subscribe to vendor socket room and listen for alert events. No polling.
  useEffect(() => {
    const vendorId = user?.id;
    if (!vendorId || !isConnected) return;

    subscribeToVendor(vendorId);

    const handleAlertNew = (payload: any) => {
      if (typeof payload?.unreadCount === 'number') {
        setUnreadCount(payload.unreadCount);
      } else {
        // Fallback: refetch if payload lacks count.
        fetchUnreadCount();
      }
    };

    const handleUnreadCount = (payload: any) => {
      if (typeof payload?.unreadCount === 'number') {
        setUnreadCount(payload.unreadCount);
      }
    };

    on(FINANCE_EVENTS.ALERT_NEW, handleAlertNew);
    on(FINANCE_EVENTS.ALERT_UNREAD_COUNT, handleUnreadCount);

    return () => {
      off(FINANCE_EVENTS.ALERT_NEW, handleAlertNew);
      off(FINANCE_EVENTS.ALERT_UNREAD_COUNT, handleUnreadCount);
      unsubscribeFromVendor(vendorId);
    };
  }, [user?.id, isConnected, subscribeToVendor, unsubscribeFromVendor, on, off, fetchUnreadCount]);

  // Close panel on outside click
  useEffect(() => {
    if (!panelOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [panelOpen]);

  const handleToggle = () => {
    setPanelOpen((prev) => !prev);
  };

  const handlePanelClose = () => {
    setPanelOpen(false);
    // Backend pushes new count on read/dismiss via socket. Fetch as safety net.
    fetchUnreadCount();
  };

  const badgeText = unreadCount > 9 ? '9+' : String(unreadCount);

  return (
    <div ref={containerRef} style={{ position: 'relative', flexShrink: 0 }}>
      <Tooltip title={`Alerts${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}>
      <button
        onClick={handleToggle}
        aria-label={`Alerts${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          borderRadius: 10,
          border: `1px solid ${theme.gray?.[300] || '#D0CCC5'}`,
          backgroundColor: panelOpen ? theme.button.primary.bg : `${theme.gray?.[100] || '#F0EDE8'}`,
          cursor: 'pointer',
          padding: 0,
          fontFamily: 'inherit',
          transition: 'all 0.15s ease',
        }}
      >
        <BellIcon style={{ width: 20, height: 20, color: panelOpen ? theme.button.primary.text : (theme.text || '#333') }} />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 18,
              height: 18,
              borderRadius: 9,
              backgroundColor: 'var(--status-bad)',
              color: 'var(--text-inverse)',
              fontSize: '0.6rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
              lineHeight: 1,
            }}
          >
            {badgeText}
          </span>
        )}
      </button>
      </Tooltip>

      <AlertPanel open={panelOpen} onClose={handlePanelClose} />
    </div>
  );
};

export default AlertBell;
